// English source strings (RF-LANG-04). Keys are stable identifiers; other locales may be
// partial and fall back to these values. Placeholders use {name}.
import { enWave2 } from "./en-wave2.ts";
import { enWave3 } from "./en-wave3.ts";
import { enModules } from "./en-modules.ts";
import { enAgents } from "./en-agents.ts";
import { enWave5 } from "./en-wave5.ts";
import { enRouting } from "./en-routing.ts";
import { enAssistant } from "./en-assistant.ts";
import { enOnboarding } from "./en-onboarding.ts";
import { enMicrosystem } from "./en-microsystem.ts";
import { enGuardian } from "./en-guardian.ts";
import { enBlocks } from "./en-blocks.ts";
import { enSecurity } from "./en-security.ts";
import { enComposer } from "./en-composer.ts";
import { enShell } from "./en-shell.ts";
import { enContent } from "./en-content.ts";
import { enDependencies } from "./en-dependencies.ts";
import { enOperations } from "./en-operations.ts";
import { enGrant } from "./en-grant.ts";
import { enSkills } from "./en-skills.ts";

export const en = {
  "project.files.exclusiveRequired": "Reading and creating files are available. Editing and deleting existing files require an isolated workspace with exclusive write access.",
  // common
  "common.all": "All",
  "common.back": "Back",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.discard": "Discard changes",
  "common.dismiss": "Dismiss",
  "common.done": "Done",
  "common.laterPhase": "arrives in a later phase",
  "common.loading": "Loading…",
  "common.notAvailableYet": "Not available yet — arrives in a later phase",
  "common.notAvailableYetBody": "This feature is not part of the current release. Nothing here is simulated.",
  "common.optional": "optional",
  "common.refresh": "Refresh",
  "common.remove": "Remove",
  "common.retry": "Retry",
  "common.retrySafe": "Retrying is safe: the same request key is reused, so nothing is duplicated.",
  "common.save": "Save",
  "common.skipToContent": "Skip to content",

  "error.generic": "Something went wrong. Please try again.",
  "error.loadFailed": "Could not load this data",
  "error.correlation": "Reference: {id}",

  "validation.required": "This field is required.",
  "validation.email": "Enter a valid e-mail address.",
  "validation.passwordLength": "Use at least {n} characters.",
  "validation.passwordMatch": "Passwords do not match.",
  "validation.repository": "Use an https://, ssh:// or git@ repository URL.",
  "validation.chooseProject": "Choose a project.",

  // navigation / shell
  "nav.main": "Main navigation",
  "nav.sidebar": "Navigation",
  "nav.home": "Home",
  "nav.projects": "Projects",
  "nav.runs": "Runs",
  "nav.prds": "PRDs",
  "nav.memory": "Memory",
  "nav.settings": "Settings",
  "nav.inbox": "Inbox",
  "nav.agents": "Agents & Models",
  "nav.skills": "Skills",
  "nav.integrations": "Integrations",
  "nav.observability": "Observability & Audit",
  "nav.recent": "Recent",
  "nav.recentEmpty": "No projects yet.",
  "nav.recentUnavailable": "Recent projects are unavailable right now.",
  "nav.resize": "Resize navigation",
  "nav.openMenu": "Open navigation",
  "nav.closeMenu": "Close navigation",
  "nav.workspace": "Workspace",
  "nav.workspaceSelector": "Workspace: {name}",
  "nav.workspaceSwitch": "Switch or create workspace",

  "user.menu": "User menu",
  "user.menuFor": "User menu for {name}",
  "user.language": "Language",
  "user.signOut": "Sign out",

  "role.admin": "Administrator",
  "role.maintainer": "Maintainer",
  "role.operator": "Operator",
  "role.reviewer": "Reviewer",
  "role.observer": "Observer",

  // connection / realtime
  "conn.region": "Connection status",
  "conn.health.checking": "Checking API…",
  "conn.health.unreachable": "API unreachable",
  "conn.health.healthy": "API healthy",
  "conn.health.degraded": "API degraded",
  "conn.health.unavailable": "API unavailable",
  "conn.health.unknown": "API state unknown",
  "conn.checkedAt": "checked {time}",
  "conn.componentsAttention": "{list} not healthy",
  "conn.live": "Live updates",
  "conn.connecting": "Connecting to live updates…",
  "conn.reconnecting": "Reconnecting…",
  "conn.offline": "Offline",
  "conn.updatedAt": "updated {time}",
  "conn.staleSince": "data may be stale since {time}",
  "conn.noDataYet": "no live data received yet",
  "conn.retryNow": "Retry now",
  "conn.staleNotice": "Live updates are interrupted. Data may be stale since {time}; it will refresh automatically when the connection returns.",
  "conn.staleNoticeNoTime": "Live updates are interrupted. Data on this page may be stale.",
  "realtime.taskMoved": "Task #{number} is now {status}.",

  // auth
  "auth.email": "E-mail",
  "auth.password": "Password",
  "auth.signIn": "Sign in",
  "auth.signInTitle": "Sign in to Primage Studio",
  "auth.signInSubtitle": "Use your organization account.",
  "auth.invalidCredentials": "E-mail or password is incorrect.",
  "auth.mfaCode": "Authentication code",
  "auth.mfaCodeHint": "Enter the 6-digit code from your authenticator app.",
  "auth.mfaCodeInvalid": "Enter the 6-digit code from your authenticator app.",
  "auth.unreachableTitle": "The API is not reachable",
  "auth.unreachableSubtitle": "The panel could not contact the server. Your work keeps running on the backend; only this view is affected.",

  "setup.title": "Set up Primage Studio",
  "setup.subtitle": "Create the organization and its first administrator.",
  "setup.setupToken": "Setup token",
  "setup.setupTokenHint": "Required on servers: the one-time token configured by the operator (SETUP_TOKEN).",
  "setup.organization": "Organization name",
  "setup.displayName": "Your name",
  "setup.confirmPassword": "Confirm password",
  "setup.passwordHint": "At least 12 characters. It is sent once over the connection and never stored in the browser.",
  "setup.mfaNext": "Next you will enroll an authenticator app: multi-factor authentication is required for administrators and maintainers.",
  "setup.submit": "Create administrator",
  "setup.failed": "Setup failed",
  "setup.alreadyDone": "This installation is already set up",
  "setup.haveAccount": "I already have an account — sign in",
  "auth.firstRun": "First run? Set up the organization",
  "mfa.signInAgain": "Sign in again with your code",

  "mfa.enrollTitle": "Protect your account",
  "mfa.enrollSubtitle": "Administrators and maintainers must use multi-factor authentication.",
  "mfa.step1": "Open an authenticator app (TOTP) on your phone.",
  "mfa.step2": "Add an account using this setup key:",
  "mfa.step3": "Enter the code the app shows to confirm.",
  "mfa.secretLabel": "Setup key",
  "mfa.openAuthenticator": "Open in authenticator app",
  "mfa.qrLabel": "Scan this QR code with your authenticator or password manager (1Password, Google Authenticator, Authy…):",
  "mfa.qrAlt": "QR code with the authenticator setup key",
  "mfa.confirm": "Confirm and continue",
  "mfa.verifyTitle": "Verify it's you",
  "mfa.verifySubtitle": "Your session is not verified yet. Sign in again and enter the code from your authenticator app.",
  "mfa.verify": "Verify",

  // home
  "home.title": "What are we building today?",
  "home.subtitle": "Describe your idea. Your agents take care of the next steps.",
  "home.inputLabel": "Describe what you want to build",
  "home.placeholder": "For example: an inventory system integrated with my marketplaces…",
  "home.attachPrd": "Attach PRD",
  "home.removeAttachment": "Remove attachment {name}",
  "home.destination": "Destination",
  "home.newProject": "New project",
  "home.existingProjects": "Existing projects",
  "home.send": "Send",
  "home.destinationNew": "Will create a new project:",
  "home.projectNamePlaceholder": "Project name",
  "home.destinationAutonomy": "autonomy: {preset}",
  "home.destinationExisting": "Will import into the existing project “{name}”.",
  "home.autonomyLabel": "Autonomy preset for the new project",
  "home.autonomyHint": "Applied to the project created from this message; you can change it later in the project settings.",
  "home.validationEmpty": "Describe what you want to build or attach a PRD.",
  "home.validationChatLike": "That looks like a chat message, not a project brief. Describe what to build, or switch to Chat to ask a question.",
  "home.validationName": "Give the new project a name.",
  "home.validationDestination": "The selected project is no longer available. Choose another destination.",
  "home.submitFailed": "Your request was not completed",
  "home.draftKept": "Your text and destination are kept. Retrying reuses the same request keys, so no duplicate project or import is created.",
  "home.created": "Project “{name}” is ready",
  "home.openProject": "Open project",
  "home.openKanban": "Open Kanban",
  "home.reviewPending": "Review PRD coverage",
  "home.suggestions": "Other ways to start",
  "home.createApp": "Create an app",
  "home.importRepo": "Import repository",
  "home.fixProblem": "Fix a problem",
  "home.continue": "Continue where you left off",
  "home.viewAll": "View all projects",
  "home.emptyTitle": "No projects yet",
  "home.emptyBody": "Start by describing an idea above, attaching a PRD or creating a project. To let agents run code, configure your infrastructure first.",
  "home.configureInfra": "Configure infrastructure",

  // projects
  "projects.title": "Your projects",
  "projects.subtitle": "From the first idea to the working application.",
  "projects.new": "New project",
  "projects.search": "Search projects",
  "projects.searchPlaceholder": "Search project…",
  "projects.filters": "Filter projects",
  "projects.filter.all": "All",
  "projects.filter.running": "Running",
  "projects.filter.needs_attention": "Needs attention",
  "projects.filter.completed": "Completed",
  "projects.countLabel": "{n} projects",
  "projects.viewMode": "View mode",
  "projects.viewGrid": "Grid view",
  "projects.viewList": "List view",
  "projects.listLabel": "Projects",
  "projects.noPreview": "No preview captured yet",
  "projects.noDescription": "No description.",
  "projects.lastActivity": "Last activity {when}",
  "projects.noActivity": "No activity yet",
  "projects.progress": "{done} of {total} required tasks accepted",
  "projects.progressShort": "{done}/{total} required tasks accepted",
  "projects.scope": "Scope: {scope}",
  "projects.noRequiredTasks": "No required tasks yet",
  "projects.activeAgents": "{n} active agents",
  "projects.action.follow": "Follow",
  "projects.action.resolve": "Resolve access",
  "projects.action.open": "Open",
  "projects.loadMore": "Load more projects",
  "projects.filterLoadedOnly": "The filter applies to the projects loaded so far.",
  "projects.loadFailed": "Could not load projects",
  "projects.emptyTitle": "No projects yet",
  "projects.emptyBody": "Create a project, or describe an idea on Home to create one with its PRD and backlog.",
  "projects.startFromHome": "Describe an idea",
  "projects.noMatchTitle": "No projects match",
  "projects.noMatchBody": "Try another search term or filter.",
  "projects.clearFilters": "Clear filters",

  "aggregate.not_started": "Not started",
  "aggregate.running": "Running",
  "aggregate.needs_attention": "Needs attention",
  "aggregate.blocked": "Blocked",
  "aggregate.completed": "Completed",
  "aggregate.paused": "Paused",

  "projectState.draft": "Draft",
  "projectState.active": "Active",
  "projectState.paused": "Paused",
  "projectState.draining": "Draining",
  "projectState.stopped": "Stopped",

  // create project / task
  "createProject.title": "New project",
  "createProject.description": "Projects are the boundary for tasks, PRDs, memory and access.",
  "createProject.importTitle": "Import repository",
  "createProject.importDescription": "Create a project linked to an existing repository. Access is checked by the readiness validation; nothing is cloned yet.",
  "createProject.repositoryHint": "Repository access is verified later by the readiness check.",
  "createProject.autonomyHint": "Observation never changes code. You can raise autonomy later.",
  "createProject.submit": "Create project",
  "createProject.failed": "The project was not created",
  "createProject.created": "Project “{name}” created.",

  "createTask.title": "New task",
  "createTask.fixTitle": "Fix a problem",
  "createTask.description": "Describe the demand. The original text is kept alongside any enrichment.",
  "createTask.project": "Project",
  "createTask.projectsFailed": "Could not load your projects.",
  "createTask.noProjects": "Create a project first — every task belongs to a project.",
  "createTask.originalKept": "Kept verbatim as the original text.",
  "createTask.evidenceHint": "Error messages, links or steps to reproduce.",
  "createTask.submit": "Create task",
  "createTask.failed": "The task was not created",
  "createTask.created": "Task #{number} created.",
  "createTask.placement": "Placement",
  "createTask.placement.backlog": "Leave in Backlog (default)",
  "createTask.placement.backlogHint": "Stays in Backlog with its calculated priority.",
  "createTask.placement.prioritized": "Prioritize — next to start",
  "createTask.placement.prioritizedHint": "Goes to the top of the queue and to To Do as soon as its dependencies allow.",
  "createTask.placement.start_now": "Start immediately",
  "createTask.placement.start_nowHint": "Prioritized, then started now if the project is running, ready and has a free slot.",
  "createTask.placement.roleHint": "Prioritizing and starting require the Maintainer role on the project.",
  "createTask.createdPrioritized": "Task #{number} created and prioritized — next to start.",
  "createTask.createdStarted": "Task #{number} created and started.",
  "createTask.resultTitlePrioritized": "Task #{number} created and prioritized",
  "createTask.resultTitleNotStarted": "Task #{number} created and prioritized, but not started",
  "createTask.resultStatus": "Current column: {status}.",
  "createTask.resultDetail": "Detail from the server",
  "placementReason.dependencies_pending": "It stays in Backlog, at the top, until its dependencies are done.",
  "placementReason.not_promotable": "It stays in Backlog, at the top: it cannot move to To Do yet.",
  "placementReason.priority_tied": "It is tied at the maximum priority; tied tasks start in arrival order.",
  "placementReason.not_ready": "The project is not ready to execute. Check the readiness items below.",
  "placementReason.engine_unavailable": "The execution engine is unavailable in this environment.",
  "placementReason.project_not_active": "The project's execution is not running. Start the project to run it first.",
  "placementReason.at_capacity": "The concurrent task limit is reached; it starts first when a slot frees up.",
  "placementReason.engine_error": "The engine refused the start; the task stays at the top of To Do.",

  "project.name": "Name",
  "project.description": "Description",
  "project.repository": "Repository URL",
  "project.baseBranch": "Base branch",
  "project.deliveryTarget": "Delivery target",
  "project.autonomy": "Autonomy preset",
  "project.maxConcurrent": "Max concurrent tasks",
  "project.timezone": "Display timezone",
  "project.autoExecute": "Start ready tasks automatically",
  "project.autoExecuteHint": "When enabled, ready tasks are admitted without confirmation per card, within the policy.",
  "project.breadcrumb": "Breadcrumb",
  "project.sections": "Project sections",
  "project.pause": "Pause",
  "project.resume": "Resume",
  "project.start": "Start",
  "project.execution.pauseRequested": "Pause requested.",
  "project.execution.resumeRequested": "Resume requested.",
  "project.execution.startRequested": "Start requested.",
  "project.executionUnavailable": "Execution control is unavailable while the project is stopping or stopped.",
  "project.versionConflict": "The project changed in the meantime. Latest data reloaded; try again.",
  "project.openPreview": "Open preview",
  "project.executionRefused": "The server did not start execution",
  "project.openReadiness": "Open readiness check",
  "project.loadFailed": "Could not load the project",
  "project.notFound": "Project not found or not accessible",
  "project.tab.overview": "Overview",
  "project.tab.kanban": "Kanban",
  "project.tab.prds": "PRDs",
  "project.files.create": "Create file",
  "project.files.delete": "Delete file",
  "project.files.check": "Check operation",
  "project.files.unknown": "The outcome is unknown. Check the operation before making another change.",
  "project.files.pending": "Waiting for the authorized runner.",
  "project.files.error": "The file operation could not complete. Check runner availability, permissions and whether the file changed.",
  "project.files.truncated": "This listing reached the safety limit. Some files are not shown.",
  "project.files.list": "Project file list",
  "project.files.empty": "No files found.",
  "project.files.path": "Relative file path (.txt, .md or .env)",
  "project.files.content": "File contents",
  "project.files.envWarning": "ENV contents are visible only in your admin session. Close this editor when finished.",
  "project.files.agents": "Request code changes through an agent task",
  "project.files.undo": "Undo last change",
  "project.files.deleteConfirm": "Delete this file? An encrypted backup is retained and you can undo this deletion.",
  "project.tab.files": "Files",
  "project.files.title": "Project files — unavailable",
  "project.files.body": "File browsing is waiting for the authorized runner connection. File creation, updates and deletion are not available yet.",
  "project.files.policy": "Edit TEXT and Markdown here. Code changes go through agents. ENV files are restricted to human admin sessions.",
  "project.tab.code": "Code",
  "project.tab.runs": "Runs",
  "project.tab.memory": "Memory",
  "project.tab.settings": "Settings",
  "project.code.title": "Code — not available yet",
  "project.code.body": "Inspecting files and diffs per version arrives with the execution engine in a later phase.",
  "project.runs.title": "Runs — not available yet",
  "project.runs.body": "Runs, attempts and full logs appear here once the execution engine is enabled in a later phase.",

  // overview
  "overview.preview": "Application preview",
  "overview.previewUnavailableTitle": "No preview available",
  "overview.previewUnavailableBody": "A preview shows a registered target of this project with its version and freshness. Register one in Preview & tests; no application image is shown until a real capture exists.",
  "overview.chat": "Chat with the project",
  "overview.chatInput": "Guidance for the project",
  "overview.chatPlaceholder": "Send guidance…",
  "overview.attach": "Attach file",
  "overview.assistantUnavailable": "Assistant not configured — arrives in a later phase",
  "overview.assistantUnavailableBody": "The project assistant needs a configured model route. Until then, create tasks from the Kanban or import a PRD.",
  "overview.steps": "Work in progress",
  "overview.stepsSummary": "{active} agents working · {waiting} waiting for resources · {failed} failed · {total} tasks outside the backlog",
  "overview.expandSteps": "Expand work in progress",
  "overview.collapseSteps": "Collapse work in progress",
  "overview.noStepsTitle": "Nothing in progress yet",
  "overview.noStepsBody": "Tasks appear here once they leave the backlog. Import a PRD or create tasks to plan the work.",
  "overview.importPrd": "Import PRD",
  "overview.openKanban": "Open Kanban",
  "overview.openInKanban": "Open in Kanban",
  "overview.timelineFailed": "Could not load the timeline",
  "overview.timelineEmpty": "No events recorded yet.",
  "overview.timelineMore": "Showing the first {n} events; more recent events are available.",
  "overview.fullLogs": "Full logs",
  "overview.attemptHistory": "Attempt history",
  "overview.usageUnknown": "Tokens and cost: not measured yet",
  "overview.panels": "Show panel",
  "overview.resizeSplit": "Resize preview and chat",

  "step.waiting": "Waiting",
  "step.in_progress": "In progress",
  "step.done": "Done",
  "step.failed": "Failed",
  "step.blocked": "Blocked",
  "step.paused": "Paused",
  "step.cancelled": "Cancelled",

  // tasks / kanban
  "status.backlog": "Backlog",
  "status.in_analysis": "In Analysis",
  "status.todo": "To Do",
  "status.in_progress": "In Progress",
  "status.testing": "Testing",
  "status.in_review": "In Review",
  "status.integrating": "Integrating",
  "status.awaiting_release": "Awaiting Merge / Release",
  "status.done": "Done",
  "status.blocked": "Blocked",
  "status.waiting_resource": "Waiting for Resource",
  "status.paused": "Paused",
  "status.failed": "Failed",
  "status.cancelled": "Cancelled",
  "status.awaiting_human": "Awaiting human decision",

  "action.start_analysis": "Start analysis",
  "action.mark_ready": "Mark ready",
  "action.start": "Start",
  "action.submit_for_testing": "Submit for testing",
  "action.gates_passed": "Gates passed",
  "action.gates_failed": "Gates failed",
  "action.review_approved": "Approve review",
  "action.review_rejected": "Reject review",
  "action.integrated": "Mark integrated",
  "action.release_confirmed": "Confirm release",
  "action.complete": "Complete",
  "action.block": "Block",
  "action.unblock": "Unblock",
  "action.wait_resource": "Wait for resource",
  "action.resource_available": "Resource available",
  "action.pause": "Pause",
  "action.resume": "Resume",
  "action.fail": "Mark failed",
  "action.retry": "Retry",
  "action.cancel": "Cancel task",
  "action.reopen": "Reopen",
  "action.admin_override": "Administrative override",
  "action.await_human": "Ask a human",
  "action.human_approved": "Approved by a human",
  "action.human_rejected": "Rejected by a human",
  "action.mark_existing": "Close as already implemented",

  "taskType.feature": "Feature",
  "taskType.bugfix": "Fix",
  "taskType.investigation": "Investigation",
  "taskType.refactor": "Refactoring",
  "taskType.test": "Test",
  "taskType.documentation": "Documentation",
  "taskType.operation": "Operations",

  "risk.low": "Low risk",
  "risk.medium": "Medium risk",
  "risk.high": "High risk",
  "risk.critical": "Critical risk",

  "origin.manual": "Manual",
  "origin.prd": "PRD",
  "origin.chat": "Chat",
  "origin.api": "API",
  "origin.github": "GitHub",
  "origin.sentry": "Sentry",
  "origin.system": "System",

  "delivery.local_commit": "Delivers: local commit",
  "delivery.pull_request": "Delivers: pull request",
  "delivery.merge_confirmed": "Delivers: confirmed merge",
  "delivery.deployment_validated": "Delivers: validated deployment",

  "autonomy.observation": "Observation",
  "autonomy.autonomous_development": "Autonomous development",
  "autonomy.autonomous_staging": "Autonomous staging",
  "autonomy.restricted_production": "Restricted production",
  "autonomy.autonomous_admin_isolated_dev": "Autonomous administrator — isolated development",

  "task.title": "Title",
  "task.descriptionField": "Description",
  "task.evidence": "Evidence",
  "task.type": "Type",
  "task.risk": "Risk",
  "task.origin": "Origin",
  "task.prd": "PRD",
  "task.priority": "Priority",
  "task.priorityShort": "Priority {score}",
  "task.dependencies": "Dependencies",
  "task.dependsOn": "Depends on {list}",
  "task.requirements": "Requirements",
  "task.dueAt": "Deadline",
  "task.agentModel": "Agent / model",
  "task.notReportedYet": "Not reported yet",
  "task.updated": "Updated",
  "task.none": "None",
  "task.fromPrd": "From PRD",
  "task.returnsTo": "Returns to {column}",
  "task.acceptance": "Acceptance checklist",
  "task.noAcceptance": "No acceptance criteria recorded.",
  "task.satisfied": "satisfied",
  "task.notSatisfied": "not satisfied",
  "task.timeline": "Timeline",
  "task.time": "Time",
  "task.priorityReason": "Priority: {reason}",
  "durations.wall": "Time in states {value}",
  "durations.effort": "Summed state intervals {value}",
  "durations.running": "open state interval",
  "precision.measured": "measured",
  "precision.estimated": "estimated",
  "precision.incomplete": "incomplete",
  "precision.unknown": "unknown precision",

  "kanban.board": "Kanban board",
  "kanban.sideLane": "Side states",
  "kanban.search": "Filter tasks",
  "kanban.newTask": "New task",
  "kanban.loadMore": "Load +{count}/{remaining}",
  "kanban.hint": "Drag a card to request a move, or use its actions menu. The server decides; refusals show their reason.",
  "kanban.actionsFor": "Actions for task #{number}",
  "kanban.requestAction": "Request an action",
  "kanban.suggestedActions": "Suggested actions (server decides)",
  "kanban.noActions": "No actions available",
  "kanban.accepted": "Task #{number} moved to {status}.",
  "kanban.refused": "Task #{number}: “{action}” was refused",
  "kanban.refusedAnnounce": "Move of task #{number} refused: {reason}",
  "kanban.moveNotPossible": "Task #{number} cannot be moved there",
  "kanban.noActionForMove": "No action moves a task from {from} to {to}. Use the actions menu to see what is possible.",
  "kanban.pendingGates": "Pending gates:",
  "kanban.prdVersionOption": "PRD version {n}",
  "kanban.emptyTitle": "No tasks yet",
  "kanban.emptyBody": "Import a PRD to plan the backlog, or create a task.",
  "kanban.confirmTitle": "{action} — task #{number}",
  "kanban.confirmCancelBody": "Cancelling keeps the full history; the task can be reopened later.",
  "kanban.confirmFailBody": "Marking as failed keeps the attempt history; the task can be retried.",
  "kanban.reason": "Reason",
  "kanban.serverDecides": "The server validates every request (gates, dependencies and version).",
  "kanban.card.workingFor": "Attempt elapsed {duration}",
  "kanban.card.waitingFor": "Attempt elapsed {duration} · Waiting for a resource",
  "kanban.card.inColumnFor": "In {column} for {duration}",
  "kanban.card.finishedAgo": "Finished {duration} ago",
  "kanban.card.noActiveAttempt": "No active agent attempt",
  "kanban.card.executionUnknown": "Agent execution not reported",
  "kanban.startPending": "Start accepted · Waiting for an agent attempt",
  "kanban.startAccepted": "Start request accepted for task #{number}. Waiting for an agent attempt.",
  "kanban.card.agent": "Agent",
  "kanban.card.model": "Model",
  "kanban.card.viaRoute": "via {model}",
  "kanban.card.modelPending": "effective model not reported yet",
  "kanban.card.attemptOf": "Attempt {number} · {role}",
  "kanban.card.score": "score {score}",
  "kanban.card.scoreDetails": "Priority score {score}: show the breakdown",

  // PRDs
  "prdImport.title": "Import PRD",
  "prdImport.newVersionTitle": "New version of “{title}”",
  "prdImport.description": "Paste text or attach Markdown, TXT, PDF or DOCX. Macros and scripts are never executed.",
  "prdImport.docTitle": "Document title",
  "prdImport.source": "Source",
  "prdImport.chooseFile": "Choose file",
  "prdImport.formats": "Markdown, TXT, PDF or DOCX · up to 10 MB",
  "prdImport.pasteText": "Or paste the text",
  "prdImport.startExecution": "Start execution after import",
  "prdImport.startExecutionHint": "Leave unchecked to import and plan without starting any task.",
  "prdImport.submit": "Import",
  "prdImport.failed": "Import failed",
  "prdImport.retryHint": "Retrying the same content reuses the same request key and never duplicates the import.",
  "prdImport.missing": "Add a title and some content.",
  "prdImport.unsupported": "Unsupported file type. Use Markdown, TXT, PDF or DOCX.",
  "prdImport.tooLarge": "The file is larger than 10 MB.",
  "prdImport.empty": "The file is empty.",
  "prdImport.readFailed": "The file could not be read. Try again or paste the text.",
  "prdImport.reading": "Reading file… {pct}%",
  "prdImport.uploading": "Uploading… {pct}%",
  "prdImport.extracting": "Extracting requirements…",
  "prdImport.version": "Version {n}",
  "prdImport.replayed": "Previous import returned",
  "prdImport.counts": "{requirements} requirements · {tasks} tasks · {pending} pending items",
  "prdImport.duplicateWarning": "The same content was imported before. This import was kept as an intentional new version.",

  "extraction.complete": "Extraction complete",
  "extraction.partial": "Partial extraction",
  "extraction.failed": "Extraction failed",
  "extraction.unsupported": "Unsupported document",

  "requirement.pending": "Pending",
  "requirement.planned": "Planned",
  "requirement.covered": "Covered",
  "requirement.ambiguous": "Ambiguous",
  "requirement.not_implementable": "Not implementable",
  "requirement.superseded": "Superseded",

  "prds.intro": "Versioned documents, extracted requirements and their coverage.",
  "prds.import": "Import PRD",
  "prds.newVersion": "New version",
  "prds.version": "Version",
  "prds.versionOption": "v{n} · {date}",
  "prds.noVersions": "No versions yet.",
  "prds.emptyTitle": "No PRDs in this project",
  "prds.emptyBody": "Import a PRD to extract requirements and plan tasks. Requirements are never dropped silently.",
  "prds.coverageSummary": "{total} requirements · {open} need attention",
  "prds.onlyOpen": "Show only pending or ambiguous",
  "prds.noRequirements": "No requirements were extracted from this version.",
  "prds.nothingOpen": "No pending or ambiguous requirements.",
  "prds.coverageCaption": "Coverage matrix: requirement, state, tasks, tests and evidence",
  "prds.requirement": "Requirement",
  "prds.state": "State",
  "prds.tasks": "Tasks",
  "prds.tests": "Tests",
  "prds.evidence": "Evidence",
  "prds.none": "None yet",
  "prds.noTasks": "No task yet",
  "prds.ambiguity": "Ambiguity",
  "prds.optionalReq": "optional",

  "prdsIndex.subtitle": "PRDs belong to projects. Choose a project to see its documents and coverage.",
  "prdsIndex.emptyTitle": "No PRDs yet",
  "prdsIndex.emptyBody": "Describe an idea or attach a PRD on Home to create a project with its PRD.",
  "prdsIndex.startHome": "Go to Home",

  // project settings
  "projectSettings.title": "Project settings",
  "projectSettings.saved": "Project settings saved.",
  "projectSettings.conflictTitle": "This project changed since you opened it",
  "projectSettings.conflictBody": "Someone else saved changes. Load the latest version, then re-apply your edits.",
  "projectSettings.loadLatest": "Load latest",
  "projectSettings.version": "Version {n}",
  "projectSettings.readiness": "Readiness check",
  "projectSettings.readinessBody": "Validates repository, branch, workspace, runner, executor, test command and model route before activation.",
  "projectSettings.runReadiness": "Run check",
  "projectSettings.operationStatus": "Check status:",
  "projectSettings.lastChecked": "Last checked {when}",
  "projectSettings.neverChecked": "Never checked",
  "projectSettings.environments": "Workspaces, environments & access",
  "projectSettings.environmentsBody": "Registering workspaces, environments and credentials per project arrives with the infrastructure integration.",

  "operation.accepted": "Accepted",
  "operation.running": "Running",
  "operation.succeeded": "Succeeded",
  "operation.failed": "Failed",

  // settings
  "settings.title": "Settings",
  "settings.subtitle": "Infrastructure, providers, models, agents, quotas and policies.",
  "settings.structureOnly": "This section shows the configuration structure only. It will be enabled with its integration; no test result here is simulated.",
  "settings.noSimulatedTests": "Connection, inference and capability tests will run only against real, configured services.",
  "settings.tab.infrastructure": "Infrastructure & runners",
  "settings.tab.providers": "Providers & keys",
  "settings.tab.models": "Models & combos",
  "settings.tab.agents": "Agents & models",
  "settings.tab.quotas": "Quotas & budget",
  "settings.tab.context": "Context & memory",
  "settings.tab.gauntlet": "Gauntlet Loop",
  "settings.tab.history": "History",
  "settings.tab.permissions": "Permissions",
  "settings.secret.notSet": "Not set",
  "settings.secret.writeOnly": "Write-only: after saving, only a masked state is shown.",

  "settings.infra.intro": "VPS, SSH, Coolify and runners.",
  "settings.infra.unavailable": "The infrastructure integration is not enabled in this release. Fields show the final structure; nothing entered here is saved or sent.",
  "settings.infra.actionsUnavailable": "Available when the infrastructure integration is enabled.",
  "settings.infra.identification": "Identification",
  "settings.infra.vpsName": "VPS name",
  "settings.infra.purpose": "Purpose",
  "settings.infra.purposeDev": "Development and testing",
  "settings.infra.provider": "Provider",
  "settings.infra.system": "Declared system and architecture",
  "settings.infra.ssh": "SSH",
  "settings.infra.host": "IP or hostname",
  "settings.infra.port": "Port",
  "settings.infra.user": "User",
  "settings.infra.authMethod": "Authentication method",
  "settings.infra.privateKey": "Private key",
  "settings.infra.password": "Password",
  "settings.infra.secrets": "SSH secrets",
  "settings.infra.secretsHint": "Stored in the vault by reference. Password and key are never required at the same time.",
  "settings.infra.sshPassword": "SSH password",
  "settings.infra.privateKeyField": "Private key",
  "settings.infra.passphrase": "Key passphrase",
  "settings.infra.sudoMode": "Elevation mode",
  "settings.infra.sudoRoot": "Root",
  "settings.infra.sudoPasswordless": "Passwordless sudo",
  "settings.infra.sudoWithPassword": "Sudo with a separate password",
  "settings.infra.sudoPassword": "Sudo password",
  "settings.infra.identity": "Server identity",
  "settings.infra.identityHint": "The host key fingerprint is recorded on first validation. A change blocks the connection for identity review.",
  "settings.infra.fingerprint": "Observed host key fingerprint",
  "settings.infra.fingerprintNone": "Not validated yet",
  "settings.infra.coolifyApi": "Coolify API",
  "settings.infra.coolifyUrl": "Coolify base URL",
  "settings.infra.coolifyToken": "Coolify API token",
  "settings.infra.coolifyWeb": "Optional Coolify web login",
  "settings.infra.coolifyWebHint": "Only needed for authenticated browsing. It never replaces the API token.",
  "settings.infra.coolifyWebEnable": "Use authenticated web access",
  "settings.infra.coolifyLoginUrl": "Login URL",
  "settings.infra.coolifyUser": "User or e-mail",
  "settings.infra.coolifyPassword": "Coolify panel password",
  "settings.infra.runner": "Runner & workspace",
  "settings.infra.runnerId": "Runner identity",
  "settings.infra.workdir": "Working directory",
  "settings.infra.operation": "Operation",
  "settings.infra.limits": "CPU / RAM / disk / concurrency limits",
  "settings.infra.limitsPlaceholder": "Set after the capacity inventory",
  "settings.infra.testSsh": "Test SSH",
  "settings.infra.testCoolify": "Test Coolify",
  "settings.infra.verifyAdmin": "Verify admin capability",
  "settings.infra.prepareRunner": "Prepare runner",

  "settings.providers.body": "OmniRoute, direct OpenRouter, direct NVIDIA NIM and the approved Jev endpoint.",
  "settings.providers.g1": "Connections: name, endpoint, authentication and secret reference",
  "settings.providers.g2": "Keys: add, replace, test and revoke (write-only)",
  "settings.providers.g3": "Connection, minimal inference and capability tests",
  "settings.models.body": "Catalog when available, manual registration and exact external IDs.",
  "settings.models.g1": "Model targets: external ID, alias and type",
  "settings.models.g2": "Declared vs verified capabilities and limits",
  "settings.models.g3": "Renaming without losing history",
  "settings.agents.body": "Each agent profile with role, main model, fallbacks and reviewer.",
  "settings.agents.g1": "Model per role matrix",
  "settings.agents.g2": "Agent profiles and tools",
  "settings.agents.g3": "Maestro and strong substitutes",
  "settings.quotas.body": "Shared pools, RPM/TPM, concurrency and spend ceilings.",
  "settings.quotas.g1": "Quota pools and windows",
  "settings.quotas.g2": "Budget ceilings and renewal",
  "settings.quotas.g3": "Behavior when a resource is missing",
  "settings.context.body": "Context window, output reservation, compactor, embedding, reranker and data policy.",
  "settings.context.g1": "Window and output reservation",
  "settings.context.g2": "Compactor, embedding and reranker",
  "settings.context.g3": "Data policy per provider",
  "settings.gauntlet.body": "Mode, builder/critic, references, limits, stagnation detection and escalation.",
  "settings.gauntlet.g1": "Builder and critic",
  "settings.gauntlet.g2": "Round limits and stagnation",
  "settings.gauntlet.g3": "Escalation rules",
  "settings.history.body": "Author, diff between versions, configuration tests, activation and rollback.",
  "settings.history.g1": "Configuration versions",
  "settings.history.g2": "Activation and rollback",
  "settings.history.g3": "Audit trail",
  "settings.permissions.body": "Roles, scopes and who may manage connections and secrets.",
  "settings.permissions.g1": "Members and roles",
  "settings.permissions.g2": "Service accounts",
  "settings.permissions.g3": "Secret access policy",

  // other sections
  "runs.subtitle": "Execution attempts across projects.",
  "runs.heading": "Runs arrive with the execution engine",
  "runs.body": "Once an executor is configured, every attempt, its steps, durations and evidence will be listed here.",
  "memory.subtitle": "Project memory, sources and evaluations.",
  "observability.subtitle": "Operational health, logs, process tracing and alerts.",
  "observability.heading": "Observability panels are not available yet",
  "observability.body": "The API health shown in the top bar is live. Logs, traces, bottlenecks and alerts arrive in a later phase.",
  "inbox.subtitle": "External deliveries (GitHub, Sentry) and their processing, including those without a confirmed project.",
  "integrations.subtitle": "GitHub App and Sentry connections, incidents and pull requests.",
  "agents.subtitle": "Capabilities, measured quality, connections and quotas.",
  "agents.heading": "Configure agents and models in Settings",
  "agents.body": "Agent profiles per role, model targets and quota pools are configured in Settings. Measured quality per model arrives in a later phase.",
  "phase.f03": "Planned for phase F03 — execution.",
  "phase.f04": "Planned for phase F04 — configuration, models and quotas.",
  "phase.f08": "Planned for phase F08 — operation.",

  "notFound.title": "Page not found",
  "notFound.body": "The address may be wrong or the item may no longer be accessible.",
  ...enWave2,
  ...enWave3,
  ...enModules,
  ...enAgents,
  ...enWave5,
  ...enRouting,
  ...enAssistant,
  ...enOnboarding,
  ...enMicrosystem,
  ...enGuardian,
  ...enBlocks,
  ...enSecurity,
  ...enComposer,
  ...enShell,
  ...enContent,
  ...enDependencies,
  ...enOperations,
  ...enGrant,
  ...enSkills,
} as const;

export type MessageKey = keyof typeof en;
